import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Home, User, History, BarChart3, LogOut, PlayCircle, Users, LayoutDashboard, BookOpen, GraduationCap, Building2 } from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import authApi from '../../services/authApi';
import { toast } from 'react-hot-toast';
import { useState, useEffect } from 'react';
import axios from 'axios';

const UserLayout = () => {
  const navigate = useNavigate();
  const { user, logout, accessToken } = useAuthStore();
  const [trainingProgress, setTrainingProgress] = useState(null);

  useEffect(() => {
    // Load training progress for managers
    if (user?.role === 'manager') {
      loadTrainingProgress();
    }
  }, [user]);

  const loadTrainingProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/training/progress', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrainingProgress(response.data.progress);
    } catch (error) {
      console.error('Error loading training progress:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout(accessToken);
      logout();
      toast.success('Выход выполнен успешно');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      logout();
      navigate('/login');
    }
  };

  const baseNavItems = [
    { to: '/dashboard', icon: Home, label: 'Главная', end: true },
    ...(user?.role === 'manager' ? [{ to: '/training-mode', icon: GraduationCap, label: 'Обучение', badge: trainingProgress && trainingProgress.overallProgress < 100 ? `${trainingProgress.overallProgress}%` : null }] : []),
    { to: '/scenarios', icon: PlayCircle, label: 'Тренировки' },
    { to: '/knowledge', icon: BookOpen, label: 'База знаний' },
    { to: '/history', icon: History, label: 'История' },
    { to: '/stats', icon: BarChart3, label: 'Статистика' },
    { to: '/profile', icon: User, label: 'Профиль' },
  ];

  // Add Team Dashboard and Leader Dashboard for supervisors and directors
  const navItems = user?.role === 'director'
    ? [
        ...baseNavItems.slice(0, 4),
        { to: '/leader', icon: LayoutDashboard, label: 'Дашборд руководителя' },
        { to: '/organization', icon: Building2, label: 'Сотрудники и франшизы' },
        ...baseNavItems.slice(4),
      ]
    : user?.role === 'supervisor'
    ? [
        ...baseNavItems.slice(0, 4),
        { to: '/leader', icon: LayoutDashboard, label: 'Дашборд руководителя' },
        { to: '/team', icon: Users, label: 'Моя команда' },
        ...baseNavItems.slice(4),
      ]
    : user?.role === 'admin'
    ? [
        ...baseNavItems.slice(0, 4),
        { to: '/team', icon: Users, label: 'Команда' },
        ...baseNavItems.slice(4),
      ]
    : baseNavItems;

  const getRoleBadge = (role) => {
    const badges = {
      admin: { class: 'bg-purple-100 text-purple-700', label: 'Администратор' },
      director: { class: 'bg-indigo-100 text-indigo-700', label: 'Директор' },
      supervisor: { class: 'bg-blue-100 text-blue-700', label: 'Супервайзер' },
      manager: { class: 'bg-green-100 text-green-700', label: 'Менеджер' },
    };
    return badges[role] || badges.manager;
  };

  const roleBadge = getRoleBadge(user?.role);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Savitskaya.ai</h1>
          <p className="text-xs text-gray-500 mt-1">Платформа обучения</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium flex-1">{item.label}</span>
              {item.badge && (
                <span className="text-xs font-semibold px-2 py-1 bg-orange-100 text-orange-600 rounded-full">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="mb-3 px-4 py-2 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </p>
            {user?.firstName && <p className="text-xs text-gray-500">{user.email}</p>}
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${roleBadge.class}`}>
              {roleBadge.label}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Выйти</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default UserLayout;
